import React, { useRef, useEffect, useState, useContext } from 'react'
import { graphql, Link } from 'gatsby'
import { RichText } from 'prismic-reactjs'
import Button from '../components/Button'
import JpegSequence from '../components/JpegSequence'
import SliderHomepage from '../components/SliderHomepage'
import ParallaxImage from '../components/ParallaxImage'
import clamp from '../helpers/clamp'
import ratio from '../helpers/ratio'
import SplitText from '../components/SplitText'
import Seo from '../components/Seo'
import { ContextLoader } from '../context';
import { detectElementsOnScreen, killObservers } from '../helpers/detectElementsOnScreen'
import { tabletP } from '../utils/bp'

const Homepage = ({ data, location }) => {
	const pageData = data.prismicHomepage.data

	const [isLoading] = useContext(ContextLoader);
	useEffect(() => {
		if (isLoading === true) return
		const [observers, elements] = detectElementsOnScreen()
		return () => {
			killObservers(observers, elements)
		}
	}, [isLoading])

	const stickyContainerEl = useRef(null)
	const scrollCtaEl = useRef(null)
	const heroHeaderSubtitleEl = useRef(null)
	const heroHeaderTitleEl = useRef(null)
	const firstImageEl = useRef(null)
	const firstSpritesContainerEl = useRef(null)
	const firstFrameEl = useRef(null)
	const firstFrameBorderLeftEl = useRef(null)
	const firstFrameBorderTopEl = useRef(null)
	const firstFrameBorderRightEl = useRef(null)
	const firstFrameBorderBottomEl = useRef(null)
	const firstTitleEl = useRef(null)
	const secondContainerEl = useRef(null)
	const secondSpritesContainerEl = useRef(null)
	const secondTitleEl = useRef(null)

	const [firstVideoScrollRatio, setFirstVideoScrollRatio] = useState(0)
	const [secondVideoScrollRatio, setSecondVideoScrollRatio] = useState(0)

	useEffect(() => {
		const isMobile = window.innerWidth <= tabletP && window.innerHeight > window.innerWidth
		let wh = 0
		const handleScroll = () => {
			const scrollY = window.scrollY
			const lengthY = stickyContainerEl.current.offsetHeight

			if (isMobile === false) {
				// Hero header texts opacity
				const progressHeroHeader = clamp(scrollY / (lengthY * 0.05), 0, 1)
				heroHeaderSubtitleEl.current.style.opacity = 1 - progressHeroHeader
				heroHeaderTitleEl.current.style.opacity = 1 - progressHeroHeader
				// Scroll cta opacity
				const progressScrollCta = clamp(scrollY / (lengthY * 0.1), 0, 1)
				scrollCtaEl.current.style.opacity = 1 - progressScrollCta
				// First image translation
				const startFirstImageTranslation = lengthY * 0.05
				const progressFirstImage = clamp((scrollY - startFirstImageTranslation) / (lengthY * 0.2 - startFirstImageTranslation), 0, 1)
				firstImageEl.current.style.transform = `translate3d(${ratio(-10) * progressFirstImage}vw, 0, 0)`
				// First sprite container translation
				const progressFirstSpriteContainer = clamp(scrollY / (lengthY * 0.3), 0, 1)
				firstSpritesContainerEl.current.style.transform = `translate3d(${ratio(-72) * progressFirstSpriteContainer}vw, 0, 0)`
				// First frame scaling to full width
				firstFrameEl.current.style.transform = `scale(${1 + 1 * progressFirstSpriteContainer}, 1)`
				// First frame scale to disappear
				const progressFirstFrame = clamp(scrollY / (lengthY * 0.3), 0, 1)
				firstFrameBorderLeftEl.current.style.transform = `translate3d(${ratio(-22.8) * progressFirstFrame}vw, 0, 0)`
				firstFrameBorderRightEl.current.style.transform = `translate3d(${ratio(22.8) * progressFirstFrame}vw, 0, 0)`
				firstFrameBorderTopEl.current.style.transform = `translate3d(0, ${-25 * progressFirstFrame}vh, 0)`
				firstFrameBorderBottomEl.current.style.transform = `translate3d(0, ${25 * progressFirstFrame}vh, 0)`
				// First video
				const startFirstVideo = 0
				setFirstVideoScrollRatio(clamp((scrollY - startFirstVideo) / (lengthY * 0.6 - startFirstVideo), 0, 1))
				// First title
				const startFirstTitle = lengthY * 0.2
				const progressFirstTitleOpacity = clamp((scrollY - startFirstTitle) / (lengthY * 0.4 - startFirstTitle), 0, 1)
				firstTitleEl.current.style.opacity = progressFirstTitleOpacity
				// const progressFirstTitleTranslate = clamp((scrollY - startFirstTitle) / (lengthY * 0.6 - startFirstTitle), 0, 1)
				// firstTitleEl.current.style.transform = `translate3d(0, ${15 * (1 - progressFirstTitleTranslate)}vh, 0)`
				// Second title
				const startSecondTitle = lengthY * 0.48
				const progressSecondTitleOpacity = clamp((scrollY - startSecondTitle) / (lengthY * 0.6 - startSecondTitle), 0, 1)
				secondTitleEl.current.style.opacity = progressSecondTitleOpacity
				// const progressSecondTitleTranslate = clamp((scrollY - startSecondTitle) / (lengthY * 0.9 - startSecondTitle), 0, 1)
				// secondTitleEl.current.style.transform = `translate3d(0, ${15 * (1 - progressSecondTitleTranslate)}vh, 0)`
				// Second video
				const startSecondVideo = lengthY * 0.5
				setSecondVideoScrollRatio(clamp((scrollY - startSecondVideo) / (lengthY - startSecondVideo), 0, 1))
			}
			else {
				// First video
				const startFirstVideo = 0.3
				setFirstVideoScrollRatio(clamp((scrollY - startFirstVideo) / (lengthY * 0.7 - startFirstVideo), 0, 1))
				// Second video
				const startSecondVideo = lengthY * 0.5
				setSecondVideoScrollRatio(clamp((scrollY - startSecondVideo) / (lengthY - startSecondVideo), 0, 1))
			}

			// Second container parallax
			const secondContainerY = secondContainerEl.current.getBoundingClientRect().y
			const progressSecondY = clamp(secondContainerY / wh, 0, 1)
			secondSpritesContainerEl.current.style.transform = `translate3d(0, ${progressSecondY * -50}vh, 0)`
		}

		const handleResize = () => {
			wh = window.innerHeight
		}

		handleResize()
		window.addEventListener('scroll', handleScroll)
		window.addEventListener('resize', handleResize)
		return () => {
			window.removeEventListener('scroll', handleScroll)
			window.removeEventListener('resize', handleResize)
		}
	}, [])

  return (
    <main className="Homepage">
			<Seo
				title=""
				description={pageData.meta_description.text}
				url={location.href}
			/>
			<div className="Homepage__HeroStickyContainer" ref={stickyContainerEl} data-menu-color="lBlack,mGrey,sGrey">
				<div className="Homepage__HeroHeaderStickyFirst">
					<section className="Homepage__HeroHeader">
						<img
							className="Homepage__HeroHeaderImage"
							alt={pageData.hero_header_image.alt}
							src={pageData.hero_header_image.fluid.src}
							srcSet={pageData.hero_header_image.fluid.srcSet}
							ref={firstImageEl}
						/>
						<div className="Homepage__HeroHeaderSubtitle caps" data-is-on-screen>
							<div ref={heroHeaderSubtitleEl}>
								<RichText render={pageData.hero_header_subtitle.raw} />
							</div>
						</div>
						<div className="Homepage__HeroHeaderTitle h1 enterByLetters" data-is-on-screen ref={heroHeaderTitleEl}>
							<SplitText splitBy="chars">
								<RichText render={pageData.hero_header_catchphrase.raw} />
							</SplitText>
						</div>
					</section>
					<section className="Homepage__FirstSection">
						<div className="Homepage__FirstSpritesContainer" ref={firstSpritesContainerEl} data-is-on-screen>
							<JpegSequence
								folder="01"
								scrollRatio={firstVideoScrollRatio}
								frameCount={150}
								className="Homepage__FirstSprites"
							/>
						</div>
						<div className="Homepage__FirstFrame" ref={firstFrameEl}>
							<div className="Homepage__FirstFrameBorder Homepage__FirstFrameBorderLeft" ref={firstFrameBorderLeftEl}></div>
							<div className="Homepage__FirstFrameBorder Homepage__FirstFrameBorderTop" ref={firstFrameBorderTopEl}></div>
							<div className="Homepage__FirstFrameBorder Homepage__FirstFrameBorderRight" ref={firstFrameBorderRightEl}></div>
							<div className="Homepage__FirstFrameBorder Homepage__FirstFrameBorderBottom" ref={firstFrameBorderBottomEl}></div>
						</div>
						<div className="Homepage__FirstSectionTitle h1" ref={firstTitleEl}>
							<RichText render={pageData.first_section_title.raw} />
						</div>
					</section>
					<div className="Homepage__ScrollCta" ref={scrollCtaEl}>
						<span className="Homepage__ScrollCtaLabel capsSmall">Scroll down</span>
						<div className="Homepage__ScrollCtaStroke"></div>
					</div>
				</div>
				<section className="Homepage__FirstSection Homepage__FirstSection--Mobile">
					<div className="Homepage__FirstSpritesContainer" data-is-on-screen>
						<JpegSequence
							folder="01"
							scrollRatio={firstVideoScrollRatio}
							frameCount={150}
							className="Homepage__FirstSprites"
						/>
					</div>
					<div className="Homepage__FirstSectionTitle h1">
						<RichText render={pageData.first_section_title.raw} />
					</div>
				</section>
				<section className="Homepage__SecondSectionInSticky">
					<div className="Homepage__SecondSectionContainer" ref={secondContainerEl}>
						<div className="Homepage__SecondSectionTitle h1" ref={secondTitleEl}>
							<RichText render={pageData.second_section_title.raw} />
						</div>
						<div className="Homepage__SecondSpritesContainer" ref={secondSpritesContainerEl}>
							<JpegSequence
								folder="02"
								scrollRatio={secondVideoScrollRatio}
								frameCount={150}
								className="Homepage__SecondSprites"
							/>
						</div>
					</div>
				</section>
			</div>
			<section className="Homepage__SectionSectionOutSticky" data-menu-color="lWhite,mGrey,sGrey">
				<div className="Homepage__SecondSectionText h4 enterByLetters" data-is-on-screen>
					<SplitText splitBy="chars">
						<RichText render={pageData.second_section_text.raw} />
					</SplitText>
				</div>
				<div className="Homepage__SecondSectionSubtitle caps">
					<RichText render={pageData.second_section_subtitle.raw} />
				</div>
			</section>
			<section className="Homepage__ThirdSection" data-menu-color="lBlack,mGrey,sGrey">
				<div className="Homepage__ThirdSectionHeader">
					<div className="Homepage__ThirdSectionCatchphrase h3 enterByLetters" data-is-on-screen>
						<SplitText splitBy="chars">
							<RichText render={pageData.third_section_catchphrase.raw} />
						</SplitText>
					</div>
					<Button
						className="Homepage__ThirdSectionButton"
						label={pageData.third_section_button_label.text}
						link={`/${pageData.third_section_button_link.slug}`}
						colorText="#4C4B42"
						colorBackground="none"
						colorBorder="rgba(72, 71, 65, 0.15)"
					/>
				</div>
				<ParallaxImage
					className="Homepage__ThirdSectionImage"
					alt={pageData.third_section_image.alt}
					src={pageData.third_section_image.fluid.src}
					srcSet={pageData.third_section_image.fluid.srcSet}
				/>
			</section>
			<div className="Homepage__FourthSection" data-menu-color="lBlack,mWhite,sWhite">
				<div className="Homepage__FourthSectionCatchphrase h4 enterByLetters" data-is-on-screen>
					<SplitText splitBy="chars">
						<RichText render={pageData.fourth_section_catchphrase.raw} />
					</SplitText>
				</div>
				<Button
					className="Homepage__FourthSectionButton"
					label={pageData.fourth_section_button_label.text}
					link={pageData.fourth_section_button_link.slug}
					isFilled
					colorText="#EAE7DD"
					colorBackground="#9E9A8E"
					colorBorder="none"
				/>
			</div>
			<section className="Homepage__Slider" data-menu-color="lWhite,mGrey,sGrey">
				<SliderHomepage slides={pageData.slides} backgroundImage={pageData.slider_background_image} />
			</section>
			<section className="Homepage__Links" data-menu-color="lBlack,mGrey,sGrey">
				<ul className="Homepage__LinksList">
					{pageData.links.map((link, index) => (
						<li className="Homepage__LinksListItem" key={`home-link-${index}`}>
							<Link to={`/${link.link.slug || ''}`}>
								<span>{link.label.text}</span>
								<svg className="Homepage__LinksListItemArrow" viewBox="0 0 33 10" fill="none"><path d="M0 5h32m0 0-4.038 4M32 5l-4.038-4"/></svg>
							</Link>
						</li>
					))}
				</ul>
			</section>
    </main>
  )
}

export default Homepage

export const query = graphql`
	query Homepage {
		prismicHomepage {
			data {
				meta_description {
					text
				}
				hero_header_subtitle {
					raw
				}
				hero_header_catchphrase {
					raw
				}
				hero_header_image {
					alt
					fluid {
						src
						srcSet
					}
				}
				first_section_title {
					raw
				}
				second_section_subtitle {
					raw
				}
				second_section_text {
					raw
				}
				second_section_title {
					raw
				}
				third_section_catchphrase {
					raw
				}
				third_section_button_label {
					text
				}
				third_section_button_link {
					slug
				}
				third_section_image {
					alt
					fluid {
						src
						srcSet
					}
				}
				links {
					label {
						text
					}
					link {
						url
					}
				}
				fourth_section_catchphrase {
					raw
				}
				fourth_section_button_label {
					text
				}
				fourth_section_button_link {
					slug
				}
				slides {
					title {
						richText
					}
					text {
						richText
					}
				}
				slider_background_image {
					alt
					fluid {
						src
						srcSet
					}
				}
				links {
					link {
						slug
					}
					label {
						text
					}
				}
			}
		}
		prismicBannerAndPopup {
			...BannerAndPopup
			...CookiesNotice
		}
		prismicNewsletter {
			...Newsletter
		}
		prismicFooter {
			...Footer
		}
	}
`